import { browserTracingIntegration, captureConsoleIntegration, captureException, httpClientIntegration, init, setContext, setTag, setUser } from "@sentry/browser";
import { chronosphereLogsClient } from "../logs/index.js";
import { isNotAuthenticatedError } from "./NotAuthenticatedError.js";
import { isNotFoundError } from "./NotFoundError.js";
import { isOfflineError } from "./OfflineError.js";
import { isFetchError } from "./FetchError.js";
import { BootFlags, buildData, getBootFlag, getTenantNamespace } from "../app-config/index.js";
const resizeObserverErrors = [
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications.'
];
const notAuthenticatedErrors = [
    'Not authenticated'
];
const ignoredErrors = [
    ...resizeObserverErrors,
    ...notAuthenticatedErrors,
    /Resource of type `classic-dashboard` with slug (.)* not found/i
];
function initializeErrorTracking() {
    if (isSentryDisabled()) return;
    try {
        const tenant = getTenantNamespace();
        let environment = 'production';
        if ('meta' === tenant) environment = 'meta';
        else if (tenant.startsWith('rc-')) environment = 'rc';
        init({
            dsn: 'https://ef4c59adbbe0434b966b631b1e0570d2@o465684.ingest.sentry.io/5478737',
            environment,
            release: buildData.gitTag,
            initialScope: {
                tags: {
                    tenant
                }
            },
            beforeSend: (event)=>{
                try {
                    submitSentryEventToChronosphereLogs(event);
                } catch (_error) {}
                return event;
            },
            integrations: [
                browserTracingIntegration(),
                captureConsoleIntegration({
                    levels: [
                        'error'
                    ]
                }),
                httpClientIntegration()
            ],
            tracesSampleRate: 0.05,
            ignoreErrors: ignoredErrors,
            denyUrls: [
                /extensions\//i,
                /^chrome:\/\//i,
                /^chrome-extension:\/\//i,
                /\/otlp\/v1\/traces/i
            ]
        });
    } catch (err) {
        console.error('Error initializing error reporting', err);
    }
}
const getErrorSeverityLevel = (error)=>{
    const ignoreLevel = 'warning';
    if (error instanceof Error) {
        if (isNotFoundError(error)) return ignoreLevel;
        if (isOfflineError(error)) return ignoreLevel;
        if (isNotAuthenticatedError(error)) return ignoreLevel;
        if ('ChunkLoadError' === error.name) return ignoreLevel;
        if (isFetchError(error)) return ignoreLevel;
    }
    return 'error';
};
function logError(error, context) {
    if (isSentryDisabled()) return void console.error(error);
    captureException(error, {
        level: getErrorSeverityLevel(error),
        ...context
    });
}
function addContextToErrorLogging(context) {
    if (isSentryDisabled()) return;
    try {
        setUser(context.user);
        setContext('graphql', context.graphql);
        setTag('internal_user', context.user.internal || false);
        setTag('sysadmin_user', context.user.isSysAdmin || false);
        setTag('fullstoryUrl', context.fullstoryUrl);
    } catch (error) {
        logError(error);
    }
}
function isSentryDisabled() {
    const sentryMode = getBootFlag(BootFlags.SENTRY_MODE);
    if ('development' === process.env.NODE_ENV || 'disabled' === sentryMode) return true;
    return false;
}
function submitSentryEventToChronosphereLogs(event) {
    const eventPayload = {
        source: 'sentry',
        message: event.message,
        level: event.level ?? 'error',
        sentry_event_id: event.event_id,
        tags: event.tags,
        request: event.request,
        happened_at: event.timestamp ? new Date(1000 * event.timestamp).toISOString() : void 0,
        contexts: event.contexts,
        release: event.release,
        extra: event.extra
    };
    chronosphereLogsClient.submitLog(eventPayload);
}
export { addContextToErrorLogging, getErrorSeverityLevel, initializeErrorTracking, logError };

//# sourceMappingURL=error-reporting.js.map